import type { Duration } from "date-fns"

interface DurationDisplay {
  days: boolean
  hours: boolean
  minutes: boolean
  seconds: boolean
}

/**
 * Formats a given duration object into a human-readable string based on the specified display options.
 *
 * @param duration - The duration object containing hours, minutes, and seconds.
 * @returns An object with a method `min` that formats the duration based on the provided display options.
 *
 * @example
 * ```typescript
 * const duration = { hours: 2, minutes: 30, seconds: 45 };
 * const formatted = formatDuration(duration).min({ hours: true, minutes: true, seconds: false });
 * console.log(formatted); // Output: "2h 30m"
 * ```
 *
 * @method min
 * @param display - An optional object specifying which parts of the duration to display.
 *                  Defaults to showing hours and minutes, but not seconds.
 * @returns A formatted string representing the duration.
 */
export const formatDuration = (duration: Duration) => ({
  min: (
    display: Partial<DurationDisplay> = { days: true, hours: true, minutes: true, seconds: false },
  ): string => {
    const parts = []
    if (display.days && duration.days) parts.push(`${duration.days} d`)
    if (display.hours && duration.hours) parts.push(`${duration.hours} h`)
    if (display.minutes && duration.minutes) parts.push(`${duration.minutes} m`)
    if (display.seconds && duration.seconds) parts.push(`${duration.seconds} s`)
    return parts.join(" ")
  },
})
